import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";

const Tooltip = ({ anchorRef, text, position = "bottom", delay = 300 }) => {
  const [coords, setCoords] = useState(null);
  const timeoutRef = useRef(null);
  const tooltipRef = useRef(null);
  const isHoveredRef = useRef(false);

  useEffect(() => {
    if (!anchorRef || !anchorRef.current) return;
    
    const anchorEl = anchorRef.current;

    const handleMouseEnter = () => {
      timeoutRef.current = setTimeout(() => {
        const rect = anchorEl.getBoundingClientRect();
        setCoords(rect);
      }, delay);
    };

    const handleMouseLeave = () => {
      clearTimeout(timeoutRef.current);
      // Only hide the tooltip if it's not being hovered
      setTimeout(() => {
        if (!isHoveredRef.current) {
          setCoords(null);
        }
      }, 50); // Small delay to allow hover detection on the tooltip
    };

    anchorEl.addEventListener("mouseenter", handleMouseEnter);
    anchorEl.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      clearTimeout(timeoutRef.current);
      anchorEl.removeEventListener("mouseenter", handleMouseEnter);
      anchorEl.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [anchorRef, delay]);

  const handleTooltipMouseEnter = () => {
    isHoveredRef.current = true;
  };

  const handleTooltipMouseLeave = () => {
    isHoveredRef.current = false;
    setCoords(null); // Hide the tooltip when the mouse leaves it
  };

  if (!coords) return null;

  const offset = 10;
  const tooltipWidth = 200; // Max width

  let top =
    position === "top"
      ? coords.top - offset
      : position === "bottom"
      ? coords.bottom + offset
      : coords.top + coords.height / 2;
  
  let left =
    position === "left"
      ? coords.left - offset
      : position === "right"
      ? coords.right + offset
      : coords.left + coords.width / 2;

  // **Ensure tooltips positioned top/bottom are correctly centered horizontally**
  const transformStyle =
    position === "top" || position === "bottom"
      ? "translateX(-50%)"
      : "translateY(-50%)";

  if (position === "bottom" || position === "top") {
    left = coords.left + coords.width / 2; // Correct horizontal alignment
  }

  // **Ensure tooltips positioned left/right are centered vertically**
  if (position === "left" || position === "right") {
    top = coords.top + coords.height / 2; // Correct vertical alignment
  }

  return ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      className={`absolute z-50 px-3 py-2 tw-paragraph-reg bg-white border border-[var(--color-gray-100)] text-[var(--color-gray-800)] rounded transition-transform duration-200`}
      style={{
        top,
        left,
        width: "max-content",
        maxWidth: tooltipWidth,
        whiteSpace: "normal",
        transform: transformStyle
      }}
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
    >
      {text}
    </div>,
    document.body
  );
};

export default React.memo(Tooltip);
